import React from 'react';
import {
  Dialog,
  DialogContent,
  Typography,
  Button,
  Box,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EventIcon from '@mui/icons-material/Event';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import '../Login/LoginPopup.css';

export function formatExpiryDateForDisplay(raw) {
  if (raw == null || raw === '') return null;
  const d = raw instanceof Date ? raw : new Date(raw);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/**
 * Same expiry UI as LoginPopup — blocks backdrop/escape; Renew goes to subscribe flow.
 */
export default function SubscriptionExpiredDialog({
  open,
  onRenew,
  expiryDateFormatted,
  planName,
}) {
  const trimmedPlan =
    typeof planName === 'string' ? planName.trim() : planName || '';
  const showPlan = Boolean(trimmedPlan);

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          return;
        }
      }}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '28px',
          maxWidth: 440,
          overflow: 'visible',
          boxShadow: '0 12px 48px rgba(26, 32, 44, 0.12)',
          background: '#FFFFFF',
        },
      }}
    >
      <DialogContent
        sx={{
          px: { xs: 3, sm: 5 },
          py: { xs: 4, sm: 5 },
          textAlign: 'center',
        }}
      >
        <Box
          className="expiry-popup-icon-wrap"
          sx={{
            position: 'relative',
            width: 96,
            height: 96,
            mx: 'auto',
            mb: 3,
          }}
        >
          <Box
            className="expiry-popup-sparkles"
            sx={{
              position: 'absolute',
              inset: -8,
              pointerEvents: 'none',
            }}
          />
          {[
            { top: '0%', left: '50%', transform: 'translateX(-50%)' },
            { bottom: '4%', left: '8%', transform: 'rotate(45deg)' },
            { bottom: '10%', right: '4%', transform: 'rotate(12deg)' },
            { top: '22%', left: '-2%', transform: 'rotate(-20deg)' },
            { top: '30%', right: '-2%', transform: 'rotate(35deg)' },
          ].map((pos, i) => (
            <Box
              key={i}
              sx={{
                position: 'absolute',
                width: 8,
                height: 8,
                bgcolor: '#FFE082',
                opacity: 0.85,
                transform: pos.transform,
                top: pos.top,
                left: pos.left,
                right: pos.right,
                bottom: pos.bottom,
                clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)',
                pointerEvents: 'none',
              }}
            />
          ))}
          <Box
            sx={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              width: 80,
              height: 80,
              borderRadius: '50%',
              border: '3px solid #FF7043',
              bgcolor: '#FFF3E0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 16px rgba(255, 87, 34, 0.2)',
            }}
          >
            <AccessTimeIcon sx={{ fontSize: 40, color: '#E65100' }} />
          </Box>
        </Box>

        <Box
          sx={{
            mb: 1.5,
            width: '100%',
            maxWidth: 400,
            mx: 'auto',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
          }}
        >
          {showPlan ? (
            <>
              <Typography
                component="h2"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '1.25rem', sm: '1.45rem' },
                  color: '#1A202C',
                  lineHeight: 1.35,
                  mb: 0.75,
                  px: { xs: 0.5, sm: 0 },
                  width: '100%',
                  textAlign: 'center',
                }}
              >
                Your{' '}
                <Box
                  component="span"
                  sx={{
                    color: '#E65100',
                    fontWeight: 800,
                    wordBreak: 'break-word',
                    hyphens: 'auto',
                  }}
                >
                  {trimmedPlan}
                </Box>
              </Typography>
              <Typography
                component="p"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '1.05rem', sm: '1.15rem' },
                  color: '#1A202C',
                  lineHeight: 1.35,
                  letterSpacing: '0.01em',
                  m: 0,
                  width: '100%',
                  textAlign: 'center',
                }}
              >
                Subscription Has Expired
              </Typography>
            </>
          ) : (
            <Typography
              component="h2"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '1.35rem', sm: '1.5rem' },
                color: '#1A202C',
                lineHeight: 1.35,
                m: 0,
                width: '100%',
                textAlign: 'center',
              }}
            >
              Your Subscription Has Expired
            </Typography>
          )}
        </Box>

        <Typography
          sx={{
            color: '#4A5568',
            fontSize: '0.95rem',
            lineHeight: 1.6,
            mb: 2.5,
            maxWidth: 360,
            mx: 'auto',
          }}
        >
          {expiryDateFormatted ? (
            <>
              Your plan expired on{' '}
              <Box component="span" sx={{ fontWeight: 700, color: '#1A202C' }}>
                {expiryDateFormatted}
              </Box>
              . Renew now to keep enjoying uninterrupted access.
            </>
          ) : (
            <>
              Your plan has expired. Renew now to keep enjoying uninterrupted
              access.
            </>
          )}
        </Typography>

        <Box
          sx={{
            bgcolor: '#FFF5F2',
            borderRadius: '18px',
            py: 1.75,
            px: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            mb: 3,
          }}
        >
          <EventIcon sx={{ fontSize: 22, color: '#FF7043' }} />
          <Typography sx={{ color: '#4A5568', fontSize: '0.9rem' }}>
            Expired on:{' '}
            {expiryDateFormatted ? (
              <Box component="span" sx={{ fontWeight: 700, color: '#FF5722' }}>
                {expiryDateFormatted}
              </Box>
            ) : (
              <Box component="span" sx={{ fontWeight: 600, color: '#FF5722' }}>
                —
              </Box>
            )}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="contained"
            onClick={onRenew}
            endIcon={<ArrowForwardIcon sx={{ fontSize: 18 }} />}
            sx={{
              minWidth: 200,
              py: 1.4,
              px: 3,
              borderRadius: '999px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.95rem',
              color: '#fff',
              background: 'linear-gradient(180deg, #FF8A65 0%, #FF5722 100%)',
              boxShadow: '0 4px 14px rgba(255, 87, 34, 0.45)',
              '&:hover': {
                background: 'linear-gradient(180deg, #FF9E7A 0%, #E64A19 100%)',
                boxShadow: '0 6px 18px rgba(255, 87, 34, 0.5)',
              },
            }}
          >
            Renew Now
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
